import { useState } from 'react';
import {
  PROFILE_VERTICALS,
  PROFILE_VERTICAL_CONFIGS,
  getAppContextForVertical,
  type ProfileVertical,
} from '@photofeeler/shared';
import { useGameStore } from '@/stores/game';
import type { TabId } from './BottomNav';

interface OnboardingProps {
  onComplete: (tab: TabId) => void;
}

export function Onboarding({ onComplete }: OnboardingProps) {
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState<ProfileVertical[]>([]);
  const { setContexts, completeOnboarding } = useGameStore();

  const contextCards = PROFILE_VERTICALS.map(id => PROFILE_VERTICAL_CONFIGS[id]);

  const toggleContext = (id: ProfileVertical) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const handleFinish = () => {
    const primaryVertical = selected[0] || 'dating';
    const chosenVerticals: ProfileVertical[] = selected.length > 0 ? selected : ['dating'];
    const contexts = chosenVerticals.map(vertical => getAppContextForVertical(vertical));
    setContexts(contexts, getAppContextForVertical(primaryVertical));
    completeOnboarding();
    onComplete('new');
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-5">
      <div className="max-w-md w-full animate-fade-in">
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-10">
          {[0, 1].map(i => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                i === step ? 'bg-rose w-6' : i < step ? 'bg-rose/40' : 'bg-surface-border'
              }`}
            />
          ))}
        </div>

        {/* Step 0: Context selection */}
        {step === 0 && (
          <div className="animate-fade-in text-center">
            <h1 className="font-sans text-2xl text-ink mb-2" style={{ fontWeight: 600 }}>
              What do you want to improve?
            </h1>
            <p className="text-sm text-ink-faint font-sans mb-8" style={{ fontWeight: 300 }}>
              Select one or more — you can change this later.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-8">
              {contextCards.map(c => (
                <button
                  key={c.id}
                  onClick={() => toggleContext(c.id)}
                  className={`card p-5 text-center transition-all duration-200 ${
                    selected.includes(c.id)
                      ? 'border-rose/40 bg-rose-faint shadow-warm'
                      : 'hover:border-ink/10'
                  }`}
                >
                  <div className="text-3xl mb-2">{c.emoji}</div>
                  <div className="text-sm font-sans font-medium text-ink">{c.label}</div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setStep(1)}
              disabled={selected.length === 0}
              className="btn-rose w-full"
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 1: Let's start */}
        {step === 1 && (
          <div className="animate-fade-in text-center">
            <div className="text-5xl mb-4">
              {PROFILE_VERTICAL_CONFIGS[selected[0] || 'dating'].emoji}
            </div>
            <h1 className="font-sans text-2xl text-ink mb-2" style={{ fontWeight: 600 }}>
              Let&apos;s start!
            </h1>
            <p className="text-sm text-ink-faint font-sans mb-3" style={{ fontWeight: 300 }}>
              Your primary focus:{' '}
              <span className="font-medium text-ink">
                {PROFILE_VERTICAL_CONFIGS[selected[0] || 'dating'].label}
              </span>
            </p>
            {selected.length > 1 && (
              <p className="text-xs text-ink-faint font-sans mb-6" style={{ fontWeight: 300 }}>
                Also tracking: {selected.slice(1).map(s => PROFILE_VERTICAL_CONFIGS[s].label).join(', ')}
              </p>
            )}
            <div className="card-rose p-6 mb-8 text-left">
              <p className="text-sm font-sans text-ink" style={{ fontWeight: 400 }}>
                Upload your first photo and get an instant AI score. Then follow the Photo Coach
                lessons to improve — most people see a 15+ point boost after applying just 2-3 tips.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep(0)} className="btn-secondary flex-1">
                Back
              </button>
              <button onClick={handleFinish} className="btn-rose flex-1">
                Get Started
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
